import React, { memo, useState } from 'react';
import { Chip, Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { 
  MagnifyingGlass, 
  Info, 
  UsersThree, 
  Hash,
  Globe,
  Buildings,
  Copy,
  Tag
} from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { resolvePreviewContact } from '../utils/previewContact';
import { toast } from 'react-toastify';

export const DynamicFieldsManager: React.FC = memo(() => {
  const { state } = useMassMailContext();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // Voorbeeldwaarden komen van hetzelfde contact als in de Voorbeeld-tab: een
  // echte ontvanger zodra die beschikbaar is, anders de voorbeeldpersoon.
  const selectedContact = resolvePreviewContact(
    state.contacts,
    state.selectedPreviewContactId,
  ).contact;

  // Basic dynamic fields (Dutch placeholders like legacy)
  const basicDynamicFields = [
    { key: 'firstName', label: 'Voornaam', placeholder: '{Voornaam}', value: selectedContact.firstName },
    { key: 'lastName', label: 'Achternaam', placeholder: '{Achternaam}', value: selectedContact.lastName },
    { key: 'name', label: 'Volledige naam', placeholder: '{VolledigeNaam}', value: selectedContact.name },
    { key: 'email', label: 'E-mailadres', placeholder: '{Email}', value: selectedContact.email },
    { key: 'phoneMobile', label: 'Mobiel nummer', placeholder: '{MobielNummer}', value: selectedContact.phoneMobile },
    { key: 'resourceId', label: 'Resource ID', placeholder: '{ResourceID}', value: selectedContact.resourceId }
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`Gekopieerd: ${text}`);
  };

  return (
    <div className="space-y-6">
      {/* Unified Available Fields */}
      <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MagnifyingGlass size={20} className="text-gray-700" />
              <h4 className="font-medium text-gray-900">Beschikbare Dynamische Velden</h4>
              <Chip size="sm" variant="flat" color="default">
                {basicDynamicFields.length + 
                 state.mentionedEntities.reduce((count, entity) => 
                   count + (entity.fields ? Object.values(entity.fields).filter((field: any) => field.emailEnabled).length : 0), 0
                 ) + 
                 state.parameters.length +
                 Object.keys(state.contextPlaceholders || {}).length} velden
              </Chip>
            </div>
            <Button
              size="sm"
              variant="flat"
              color="primary"
              startContent={<Info size={16} />}
              onPress={() => setIsInfoModalOpen(true)}
            >
              Info
            </Button>
          </div>

          <div className="space-y-2">
            {/* Context Placeholders (from helpdesk, insurance, etc.) */}
            {state.contextPlaceholders && Object.entries(state.contextPlaceholders).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 cursor-pointer transition-colors"
                onClick={() => copyToClipboard(`{${key}}`)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Tag size={14} className="text-orange-600" />
                    <code className="bg-orange-100 px-2 py-1 rounded-sm text-orange-900 text-sm font-mono">
                      {`{${key}}`}
                    </code>
                    <span className="text-sm text-gray-600">
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                    </span>
                    <Chip size="sm" variant="flat" className="bg-orange-200 text-orange-800">Context</Chip>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 ml-6">
                    Waarde: {value || '[Leeg]'}
                  </div>
                </div>
                <Copy size={16} className="text-gray-400" />
              </div>
            ))}

            {/* Contact Fields */}
            {basicDynamicFields.map((field) => (
              <div
                key={field.key}
                className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors"
                onClick={() => copyToClipboard(field.placeholder)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <UsersThree size={14} className="text-blue-600" />
                    <code className="bg-blue-100 px-2 py-1 rounded-sm text-blue-900 text-sm font-mono">
                      {field.placeholder}
                    </code>
                    <span className="text-sm text-gray-600">
                      {field.label}
                    </span>
                    <Chip size="sm" variant="flat" color="primary">Contact</Chip>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 ml-6">
                    Voorbeeld: {field.value || '[Leeg]'}
                  </div>
                </div>
                <Copy size={16} className="text-gray-400" />
              </div>
            ))}

            {/* Entity Fields - Updated to use entity.fields from search endpoint */}
            {state.mentionedEntities.map((entity, idx) => {
              if (!entity.fields || !entity.uniqueId) return null;
              
              return Object.entries(entity.fields).map(([fieldName, fieldData]: [string, any], fieldIdx) => {
                if (!fieldData.emailEnabled) return null;
                
                const placeholder = `{${entity.uniqueId}.${fieldName}}`;
                const value = fieldData.value || '';
                
                return (
                  <div
                    key={`${idx}-${fieldIdx}`}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 cursor-pointer transition-colors"
                    onClick={() => copyToClipboard(placeholder)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Buildings size={14} className="text-green-600" />
                        <code className="bg-green-100 px-2 py-1 rounded-sm text-green-900 text-sm font-mono">
                          {placeholder}
                        </code>
                        <span className="text-sm text-gray-600">
                          {fieldData.dutchName || fieldName}
                        </span>
                        <Chip size="sm" variant="flat" color="success">
                          {entity.name}
                        </Chip>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 ml-6">
                        Waarde: {value || '[Leeg]'}
                      </div>
                    </div>
                    <Copy size={16} className="text-gray-400" />
                  </div>
                );
              }).filter(Boolean);
            })}

            {/* Parameter Fields */}
            {state.parameters.map((param) => (
              <div
                key={param.id}
                className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 cursor-pointer transition-colors"
                onClick={() => copyToClipboard(param.placeholder)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Hash size={14} className="text-purple-600" />
                    <code className="bg-purple-100 px-2 py-1 rounded-sm text-purple-900 text-sm font-mono">
                      {param.placeholder}
                    </code>
                    <span className="text-sm text-gray-600">
                      {param.name}
                    </span>
                    <Chip size="sm" variant="flat" color={param.type === 'global' ? 'warning' : 'secondary'}>
                      {param.type === 'global' ? (
                        <><Globe size={12} className="mr-1" />Globaal</>
                      ) : (
                        <>Individueel</>
                      )}
                    </Chip>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 ml-6">
                    {param.type === 'global' 
                      ? `Waarde: ${(state.parameterValues[param.id] as string) || '[Leeg]'}`
                      : 'Waarde per contact'
                    }
                  </div>
                </div>
                <Copy size={16} className="text-gray-400" />
              </div>
            ))}

            {/* Empty State */}
            {basicDynamicFields.length === 0 && 
             state.mentionedEntities.reduce((count, entity) => count + (entity.fields ? Object.values(entity.fields).filter((field: any) => field.emailEnabled).length : 0), 0) === 0 && 
             state.parameters.length === 0 &&
             Object.keys(state.contextPlaceholders || {}).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <MagnifyingGlass size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Geen dynamische velden beschikbaar</p>
                <p className="text-xs mt-1">
                  Voeg entiteiten toe of maak custom parameters aan
                </p>
              </div>
            )}
          </div>
      </div>

      {/* Info Modal */}
      <Modal 
        isOpen={isInfoModalOpen} 
        onClose={() => setIsInfoModalOpen(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-2">
            <Info size={24} className="text-blue-600" />
            <span>Dynamische velden gebruiken</span>
          </ModalHeader>
          <ModalBody>
            <div className="text-sm space-y-3">
              <p>
                <strong>Klik op de velden</strong> hieronder om ze naar het klembord te kopiëren en plak ze dan in uw email onderwerp of inhoud. 
                Je kunt ook handmatig typen zoals <code className="bg-gray-100 px-1 rounded-sm text-gray-900">{'{Voornaam}'}</code>.
              </p>
              <p>
                <strong>Preview selecteren:</strong> Kies in het dropdown menu boven het preview venster welk contact 
                je wilt gebruiken voor de voorvertoning. Je kunt kiezen uit echte contactpersonen of de dummy data.
              </p>
              <p>
                <strong>Gekleurde velden:</strong> In het preview venster zie je:
                <span className="inline-block bg-yellow-100 border border-yellow-300 text-yellow-800 px-1 mx-1 rounded-sm text-xs">contact velden</span>
                <span className="inline-block bg-blue-100 border border-blue-300 text-blue-800 px-1 mx-1 rounded-sm text-xs">entiteit velden</span>
                <span className="inline-block bg-green-100 border border-green-300 text-green-800 px-1 mx-1 rounded-sm text-xs">ingevulde parameters</span>
                <span className="inline-block bg-red-100 border border-red-300 text-red-800 px-1 mx-1 rounded-sm text-xs">lege parameters</span>
              </p>
              <p>
                <strong>Parameters:</strong> Gebruik <code className="bg-gray-100 px-1 rounded-sm text-gray-900">{'{gl_veldnaam}'}</code> voor globale velden 
                en <code className="bg-gray-100 px-1 rounded-sm text-gray-900">{'{ind_veldnaam}'}</code> voor individuele velden per contact.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={() => setIsInfoModalOpen(false)}>
              Begrepen
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
});
